import { SortingFn } from "@tanstack/react-table";
import { camelCase } from "lodash";

export const uncapitalize = (value: string): string =>
  value.charAt(0).toLowerCase() + value.slice(1);

export const capitalizeFirst = (value: string): string =>
  value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value;

export const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

export function enumToString<T extends { [key: string]: number | string }>(
  enumObj: T,
  value: T[keyof T]
): keyof T | "" {
  const index = Object.values(enumObj).indexOf(value);
  return index !== -1 ? (Object.keys(enumObj)[index] as keyof T) : "";
}

export function getEnumTKey<T extends { [key: string]: number | string }>(
  entityType: string,
  valueType: string,
  enumObj: T,
  enumValue: T[keyof T]
): string {
  const enumKey = enumToString(enumObj, enumValue);
  if (!enumKey) {
    return "unknown";
    // The right thing would be to throw an exception but it'll be unhandled right now and break the page
    // throw new Error(`Invalid enum value: ${enumValue}`);
  }
  const lowerCamelCaseValue = camelCase(enumKey.toString());
  return `${entityType}.${valueType}.${lowerCamelCaseValue}`;
}

export function mapEnum<T extends { [key: string]: string | number }>(
  enumObject: T,
  fn: (value: T[keyof T], key: keyof T) => any
): any[] {
  return (Object.keys(enumObject) as Array<keyof T>)
    .filter((key) => typeof enumObject[key] === "number")
    .map((key) => fn(enumObject[key] as T[keyof T], key));
}

export function createGetFieldKey<T extends { id?: string }>(
  entity: T | undefined
) {
  return (fieldName: keyof T) => {
    return `${String(fieldName)}-${entity?.id}-${entity?.[fieldName] ?? "empty"}`;
  };
}

// createGetFieldKey bakes the value into the key, remounting on every change —
// which an uncontrolled input needs, but which wipes a popover-driven control's
// open state mid-interaction. This key changes only with the field or record.
export function createGetStableFieldKey<T extends { id?: string }>(
  entity: T | undefined
) {
  return (fieldName: keyof T) => {
    return `${String(fieldName)}-${entity?.id ?? "empty"}`;
  };
}

export function normalizeStringValue(value: string): string;
export function normalizeStringValue<T>(value: T): T;
export function normalizeStringValue(value: unknown) {
  if (typeof value === "string") {
    return value.trim();
  }

  return value;
}

export const stripMarkdownLinks = (text: string): string =>
  text.replace(/\[([^\[\]]*(?:\[[^\[\]]*\][^\[\]]*)*)\]\([^)]+\)/g, "$1");

// textContent alone drops the visual break between adjacent block elements
// (e.g. `<p>A</p><p>B</p>` -> "AB"), so insert a space at each block/line
// boundary before extracting text.
const HTML_BLOCK_BOUNDARY_REGEX =
  /(<br\s*\/?>|<\/(?:p|div|li|tr|h[1-6]|blockquote)>)/gi;

export const stripHtml = <T,>(input: T): T => {
  if (typeof input !== "string") return input;
  const withBoundaries = input.replace(HTML_BLOCK_BOUNDARY_REGEX, "$1 ");
  const parsedDocument = new DOMParser().parseFromString(
    withBoundaries,
    "text/html"
  );
  return (
    (parsedDocument.body.textContent || "").replace(/ {2,}/g, " ").trim()
  ) as T;
};

const parseMonetaryValue = (value: unknown): number => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    return Number(value.replace(/[^0-9.-]+/g, "")) || 0;
  }
  return 0;
};

export const monetarySortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId);
  const bValue = rowB.getValue(columnId);
  const a = parseMonetaryValue(aValue);
  const b = parseMonetaryValue(bValue);

  return a < b ? -1 : a > b ? 1 : 0;
};

export const booleanSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId);
  const bValue = rowB.getValue(columnId);
  const aBool = aValue && Array.isArray(aValue) ? aValue.length > 0 : !!aValue;
  const bBool = bValue && Array.isArray(bValue) ? bValue.length > 0 : !!bValue;
  if (aBool && !bBool) return -1;
  if (!aBool && bBool) return 1;
  return 0;
};

interface EnumTextSortingConfig<T extends { [key: string]: number | string }> {
  entityType: string;
  valueType: string;
  enumObj: T;
  columnKey: string;
  translationFn: (key: string) => string;
}

export function createEnumTextSortingFn<
  T extends { [key: string]: number | string }
>(config: EnumTextSortingConfig<T>): SortingFn<any> {
  const { entityType, valueType, enumObj, columnKey, translationFn } = config;

  return (rowA, rowB, _columnId) => {
    const aValueKey = getEnumTKey(
      entityType,
      valueType,
      enumObj,
      rowA.original[columnKey]
    );
    const bValueKey = getEnumTKey(
      entityType,
      valueType,
      enumObj,
      rowB.original[columnKey]
    );

    const aText = translationFn(aValueKey).toLowerCase();
    const bText = translationFn(bValueKey).toLowerCase();
    return aText.localeCompare(bText);
  };
}
